import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Favorite,
  AutoAwesome,
  Chat,
  AssignmentTurnedIn,
  Home,
  ArrowForward,
  Close,
  AccountBalance,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import { Property, Match } from '../types';
import { AuthService } from '../services/auth';
import { Timestamp } from 'firebase/firestore';
import { motion } from 'framer-motion';

const BuyerDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { properties, matches } = useAppStore();
  
  const [loading, setLoading] = useState(true);
  const [financingOpen, setFinancingOpen] = useState(false);
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState('');
  const [isSavingFinancing, setIsSavingFinancing] = useState(false);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) return <LinearProgress />;

  // Real logic for favorites and matches
  const favoriteIds = user?.profile.favorites || [];
  const favoriteProperties = properties.filter(p => favoriteIds.includes(p.id));
  const aiMatches = matches.slice(0, 3); // Top 3 matches

  const hasAccord = user?.profile.financingStatus?.hasAccordDePrincipe;
  const financingProgress = hasAccord ? 100 : 0;

  const handleSaveFinancing = async () => {
    if (!user) return;
    setIsSavingFinancing(true);
    try {
      await AuthService.updateUserProfile(user.id, {
        ...user.profile,
        financingStatus: {
          hasAccordDePrincipe: true,
          bankName,
          amount: parseFloat(amount),
          verifiedAt: Timestamp.now()
        }
      });
      // User store will need refresh, but for UI we can close
      setFinancingOpen(false);
      window.location.reload(); // Simple refresh to see changes
    } catch (error) {
      console.error('Error saving financing:', error);
    } finally {
      setIsSavingFinancing(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif', color: 'primary.main' }}>
            Welcome Home, {user?.profile.firstName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your personalized Moroccan real estate headquarters.
          </Typography>
        </Box>
        <Avatar 
          src={user?.profile.avatar || undefined} 
          sx={{ width: 64, height: 64, boxShadow: 3, border: '2px solid white' }}
        >
          {user?.profile.firstName?.[0]}
        </Avatar>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {/* Top AI Matches (Luxury Card Style) */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AutoAwesome color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>Curated for You</Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/matches')}>All Recommendations</Button>
            </Box>
            
            <Grid container spacing={3}>
              {aiMatches.length > 0 ? aiMatches.map((match: any) => {
                const prop = properties.find(p => p.id === match.propertyId);
                return (
                  <Grid item xs={12} key={match.id}>
                    <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
                      <Card sx={{ display: 'flex', borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Box 
                          sx={{ 
                            width: { xs: 120, sm: 200 }, 
                            bgcolor: 'grey.100',
                            backgroundImage: `url(${prop?.metadata.images?.[0] || 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80'})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }} 
                        />
                        <CardContent sx={{ flex: 1, p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{prop?.metadata.title || 'Exclusive Unit'}</Typography>
                              <Typography variant="caption" color="text.secondary">{prop?.location.city}, {prop?.location.neighborhood}</Typography>
                            </Box>
                            <Chip 
                              label={`${match.score}% Match`} 
                              color="primary" 
                              size="small" 
                              sx={{ fontWeight: 700, borderRadius: 1.5 }} 
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" paragraph noWrap sx={{ maxWidth: 400 }}>
                            Perfectly aligns with your preference for {prop?.metadata.bedrooms} bedrooms in {prop?.location.city}.
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 800 }}>
                              {prop?.metadata.price.toLocaleString()} {prop?.metadata.currency}
                            </Typography>
                            <Button size="small" variant="contained" onClick={() => navigate(`/properties/${prop?.id}`)}>View Details</Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              }) : (
                <Grid item xs={12}>
                  <Box sx={{ py: 6, textAlign: 'center', bgcolor: 'rgba(197, 160, 89, 0.03)', borderRadius: 4, border: '1px dashed rgba(197, 160, 89, 0.2)' }}>
                    <AutoAwesome sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">AI is analyzing the market for you. Keep browsing to help it learn!</Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/properties')}>Explore Listings</Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Favorites Grid */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Favorite color="error" sx={{ fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>Your Collection</Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/properties')}>Marketplace</Button>
            </Box>
            <Grid container spacing={3}>
              {favoriteProperties.length > 0 ? favoriteProperties.map((prop) => (
                <Grid item xs={12} sm={6} key={prop.id}>
                  <Card variant="outlined" sx={{ borderRadius: 3, transition: 'all 0.3s', '&:hover': { boxShadow: 4, borderColor: 'primary.main' } }}>
                    <Box sx={{ height: 160, backgroundImage: `url(${prop.metadata.images?.[0]})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>{prop.metadata.title}</Typography>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 800, mb: 2 }}>{prop.metadata.price.toLocaleString()} {prop.metadata.currency}</Typography>
                      <Button fullWidth variant="outlined" size="small" onClick={() => navigate(`/properties/${prop.id}`)}>Open Listing</Button>
                    </CardContent>
                  </Card>
                </Grid>
              )) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Start bookmarking properties to see them here.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Readiness Status Card */}
          <Card sx={{ mb: 4, bgcolor: '#3C3E3F', color: 'white', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AccountBalance sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Buying Readiness</Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>Accord de Principe (Bank)</Typography>
                  <Typography variant="caption" sx={{ color: hasAccord ? 'success.main' : 'warning.main', fontWeight: 800 }}>
                    {hasAccord ? 'VERIFIED' : 'PENDING'}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={financingProgress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4, 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    '& .MuiLinearProgress-bar': { bgcolor: hasAccord ? '#8A9A5B' : 'primary.main' } 
                  }} 
                />
              </Box>

              {hasAccord ? (
                <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, mb: 3 }}>
                  <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>Approved by:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{user?.profile.financingStatus?.bankName}</Typography>
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 800, mt: 1 }}>
                    {user?.profile.financingStatus?.amount?.toLocaleString()} MAD
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
                  Having a bank "Accord de Principe" increases your match quality and visibility to agents.
                </Typography>
              )}

              <Button 
                variant="contained" 
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }} 
                fullWidth
                onClick={() => setFinancingOpen(true)}
              >
                {hasAccord ? 'Update Financing' : 'Verify Financing'}
              </Button>
            </CardContent>
          </Card>

          {/* AI Advisor History Scaffolding */}
          <Card variant="outlined" sx={{ borderRadius: 4, border: '1px dashed', borderColor: 'primary.main', bgcolor: 'rgba(197, 160, 89, 0.02)' }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Chat color="primary" sx={{ fontSize: 48, mb: 2, opacity: 0.6 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>AI Advisor Conversations</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                All your chats about property yields, financing, and MRE guarantees are securely saved here.
              </Typography>
              <Button variant="outlined" fullWidth sx={{ borderRadius: 2 }}>Resume Last Chat</Button>
            </CardContent>
          </Card>
          
          <Box sx={{ mt: 4 }}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUp color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Market Insight</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Properties in your favorite area (Souissi) have appreciated by 4.2% since January 2026.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Financing Dialog */}
      <Dialog open={financingOpen} onClose={() => setFinancingOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800 }}>
          Financing Verification
          <IconButton size="small" onClick={() => setFinancingOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter details from your bank's 'Accord de Principe' to qualify your buyer profile.
          </Typography>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField 
              label="Bank Name" 
              fullWidth 
              placeholder="e.g. Attijariwafa Bank, BCP..."
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
            />
            <TextField 
              label="Approved Amount (MAD)" 
              type="number" 
              fullWidth 
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setFinancingOpen(false)} color="inherit">Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveFinancing} 
            disabled={!bankName || !amount || isSavingFinancing}
            startIcon={isSavingFinancing ? <LinearProgress sx={{ width: 20 }} /> : <AssignmentTurnedIn />}
          >
            Save Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BuyerDashboardPage;
